import { HelpCircle, Lightbulb, Info } from "lucide-react";
import { CollapsibleText } from "@/components/CollapsibleText";
import { useRegisterToolInfo } from "@/components/ToolInfoContext";
import { NoticeBox } from "@/components/NoticeBox";

interface ToolInfoSectionProps {
  whatIsIt: string;
  howToUse: string;
  tip?: string;
  /** A plain, non-blocking note shown right under "How to use it" --
   *  originally, tools needing this (legal document generators) gated
   *  their entire UI behind a modal the person had to click through
   *  before seeing anything, which read as "something's wrong with this
   *  tool" before they'd had any chance to trust it. Same information,
   *  presented as a normal part of the page instead of a barrier in
   *  front of it -- deliberately not using the same warning-triangle
   *  icon a blocking version might use, since the goal here is to
   *  inform, not to make someone hesitate before they've even seen the
   *  tool. */
  disclaimer?: string;
}

/**
 * Real, genuine explanatory content for a tool page -- not filler.
 * Deliberately co-located inside each tool's own already-lazy-loaded
 * component file (imported nowhere else) so this content adds zero bytes
 * to any other page's bundle. Each tool page is its own code-split chunk;
 * this just makes that chunk a few KB bigger, invisible to load time,
 * and gives Google's reviewers real, unique, substantive content per page
 * instead of a bare calculator widget.
 */
export function ToolInfoSection({ whatIsIt, howToUse, tip, disclaimer }: ToolInfoSectionProps) {
  // On a tool page the text is handed to the shared info panel (same place
  // for every tool). The inline rendering below is only a fallback for any
  // place that shows a tool outside ToolPage.
  const collectedByPage = useRegisterToolInfo({ whatIsIt, howToUse, tip, disclaimer });
  if (collectedByPage) return null;
  return (
    <div className="mt-10 pt-8 border-t border-border/60 space-y-6">
      <div>
        <h2 className="flex items-center gap-2 text-base font-semibold mb-2">
          <HelpCircle className="h-4 w-4 text-primary" />
          What is this tool?
        </h2>
        <CollapsibleText lines={2}>{whatIsIt}</CollapsibleText>
      </div>
      <div>
        <h2 className="text-base font-semibold mb-2">How to use it</h2>
        <CollapsibleText lines={2}>{howToUse}</CollapsibleText>
      </div>
      {disclaimer && (
        <NoticeBox className="flex gap-2">
          <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <p className="text-sm">{disclaimer}</p>
        </NoticeBox>
      )}
      {tip && (
        <div className="flex gap-2 p-3 rounded-lg bg-primary/5 border border-primary/10">
          <Lightbulb className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <CollapsibleText lines={2} className="text-muted-foreground">{tip}</CollapsibleText>
        </div>
      )}
    </div>
  );
}
